import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useMutation } from '@apollo/client'
import { Link } from 'react-router-dom'
import { Field, Formik } from 'formik'
import { useCustomer } from 'lib/core/hooks'
import { Button, Form, Toast } from 'lib/ui/components'
import { FlexWrapper } from 'lib/ui/components/FlexWrapper'
import { updateCustomer, updateCustomerPassword } from 'services/customerData'
import { validation, FormValues } from './schema'
import { Container, ColumnsWrapper } from './components/Styled'
import { Title } from '../../components/Styled'

export const About = () => {
  const intl = useIntl()
  const [editInformation, setEditInformation] = useState(false)
  const [editEmail, setEditEmail] = useState(false)
  const [editPassword, setEditPassword] = useState(false)
  const { isFetching, customer, fetchCustomer } = useCustomer()
  const { firstName, lastName, email } = customer ?? {}
  const [updatePassword, { loading: updatePasswordLoading }] = useMutation(updateCustomerPassword)
  const [updateCustomerData, { loading: updateCustomerDataLoading }] = useMutation(updateCustomer)

  const handleSubmit = (values: FormValues) => {
    const data = { ...values }

    try {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      updateCustomerData({
        variables: {
          firstname: data?.firstName,
          lastname: data?.lastName,
          email: data?.email,
          password: data?.currentPassword
        }
      }).then(() => {
        Toast.toastSuccess({ messageId: 'customer.data.update.success' })
        fetchCustomer()
        setEditInformation(false)
      })
      if (editPassword) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        updatePassword({
          variables: {
            currentPassword: data?.currentPassword,
            newPassword: data?.password
          }
        }).then(() => {
          Toast.toastSuccess({ messageId: 'customer.password.update.success' })
          fetchCustomer()
          setEditInformation(false)
        })
      }
    } catch {
      Toast.toastFailure({ messageId: 'customer.password.update.error' })
    }
  }

  const schema = {
    firstName: customer?.firstName,
    lastName: customer?.lastName,
    email: customer?.email,
    currentPassword: '',
    password: '',
    passwordRetype: '',
    editPassword: false,
    editEmail: false
  }

  return (
    <Container>
      <div>
        <FlexWrapper className='flex-title'>
          <Title>
            <FormattedMessage
              id='customer.account.about.title'
            />
          </Title>
          <Link to='/customer/account'>Back</Link>
        </FlexWrapper>
        {!editInformation && (
          <div>
            <h2>Contact Information</h2>
            <p>{firstName} {lastName}</p>
            <p>{email}</p>
            <p>
              <span
                onClick={() => setEditInformation(true)}
                className='clickable'
              >
                Edit
              </span>
            </p>
          </div>
        )}
        {editInformation && (
          <>
            <p>
              <strong>
                <FormattedMessage
                  id='customer.account.personal.information'
                />
              </strong>
            </p>

            <Formik
              initialValues={schema}
              validationSchema={validation(intl)}
              onSubmit={handleSubmit}
            >
              {(formikProps: any) => {
                const { setFieldValue, values, isValid } = formikProps

                return (
                  <Form.Wrapper>
                    <ColumnsWrapper>
                      <div className='l-column'>
                        <div>
                          <Field
                            name='firstName'
                            label={intl.formatMessage({
                              id: 'customer.account.create.label.firstName'
                            })}
                            component={Form.Input}
                            isRequired
                            autoFocus
                            value={customer?.firstName}
                          />
                        </div>
                        <div>
                          <Field
                            name='lastName'
                            label={intl.formatMessage({
                              id: 'customer.account.create.label.lastName'
                            })}
                            component={Form.Input}
                            isRequired
                            value={customer?.lastName}
                          />
                        </div>
                        <div className='actions'>
                          <label>
                            <Field
                              type='checkbox'
                              name='editEmail'
                              checked={editEmail}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setEditEmail(e.target.checked)
                                setFieldValue('editEmail', !values.editEmail)
                              }}
                            />
                            <span>
                              Change Email
                            </span>
                          </label>
                          <label>
                            <Field
                              type='checkbox'
                              name='editPassword'
                              checked={editPassword}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setEditPassword(e.target.checked)
                                setFieldValue('editPassword', !values.editPassword)
                              }}
                            />
                            <span>
                              Change Password
                            </span>
                          </label>
                        </div>

                      </div>
                      <div className='r-column'>
                        {editEmail && (
                          <Field
                            name='email'
                            label={intl.formatMessage({
                              id: 'customer.account.create.label.email'
                            })}
                            component={Form.Input}
                            isRequired
                          />
                        )}
                        {(editEmail || editPassword) && (
                          <Field
                            name='currentPassword'
                            label={intl.formatMessage({
                              id: 'customer.account.create.label.current.password'
                            })}
                            component={Form.Input}
                            type='password'
                            isRequired
                          />
                        )}
                        {editPassword && (
                          <>
                            <Field
                              name='password'
                              label={intl.formatMessage({
                                id: 'customer.account.create.label.new.password'
                              })}
                              component={Form.Input}
                              type='password'
                              isRequired
                            />

                            <Field
                              name='passwordRetype'
                              label={intl.formatMessage({
                                id: 'customer.account.create.label.passwordRetype'
                              })}
                              component={Form.Input}
                              type='password'
                              isRequired
                            />
                          </>
                        )}
                      </div>
                    </ColumnsWrapper>

                    <Form.Actions.Wrapper>
                      <Button
                        type='submit'
                        isLoading={isFetching || updateCustomerDataLoading || updatePasswordLoading}
                        isDisabled={!isValid}
                      >
                        <FormattedMessage id='action.save.account' />
                      </Button>
                    </Form.Actions.Wrapper>
                  </Form.Wrapper>
                )
              }}
            </Formik>
          </>
        )}
      </div>
    </Container>
  )
}
